import { z } from 'zod';
import { isAnimal } from './cast.js';
import { contentStatusSchema, lessonCastSchema, lessonVersionReasonSchema, publishEventKindSchema, reviewStatusSchema, } from './content.js';
import { castAssignmentSchema, curriculumLessonIdSchema, lessonCastIdsSchema, storyFiguresSchema, writerNotesSchema, } from './generation.js';
/**
 * The curriculum plan — the milestone's planning tables, not live content. A planned
 * course, module or lesson becomes a `Collection` or a `Lesson` only through an explicit
 * build; importing the CSV writes these and nothing else.
 */
/** The ten named columns of a planned lesson, in the order the CSV and the export use. */
export const plannedFieldSchema = z
    .enum([
    'lessonId',
    'course',
    'module',
    'name',
    'description',
    'writerBrief',
    'primarySourceName',
    'primarySourceUrl',
    'additionalSourceName',
    'additionalSourceUrl',
])
    .describe('A named curriculum column.');
/** The header each field is read from and exported under. */
export const PLANNED_COLUMNS = {
    lessonId: 'Lesson ID',
    course: 'Course Name',
    module: 'Module Name',
    name: 'Lesson Name',
    description: 'Lesson Description',
    writerBrief: 'Writer Brief',
    primarySourceName: 'Primary Source Name',
    primarySourceUrl: 'Primary Source URL',
    additionalSourceName: 'Additional Source Name',
    additionalSourceUrl: 'Additional Source URL',
};
/** What places a lesson in the plan. The rest can be filled in later. */
export const REQUIRED_FIELDS = [
    'lessonId',
    'course',
    'module',
    'name',
];
export const URL_FIELDS = [
    'primarySourceUrl',
    'additionalSourceUrl',
];
/** Spec §8. Calculated from the linked job and lesson, never stored. */
export const plannedLessonStatusSchema = z
    .enum([
    'NOT_BUILT',
    'QUEUED',
    'BUILDING',
    'BUILT_DRAFT',
    'IN_REVIEW',
    'PUBLISHED',
    'BUILD_FAILED',
])
    .describe('Where the lesson is between the plan and the app.');
/** Where a lesson's build is, in the words Curriculum shows — GEN-134. */
export const buildStepSchema = z
    .enum([
    'CHECKING_STORY',
    'BUILDING_SCENES',
    'CHECKING_CONTINUITY',
    'DRAFT_READY',
])
    .describe('Read off its newest build: the outline, its own row, then the module review, then done. Null while queued behind other work, and with no build.');
/** A column the file has beyond the ten named ones, kept verbatim and in file order. */
export const extraColumnSchema = z.strictObject({
    column: z.string().min(1).describe('The header, as the file wrote it.'),
    value: z.string().describe('The cell, as the file wrote it.'),
});
export const curriculumImportSchema = z
    .strictObject({
    csv: z
        .string()
        .min(1)
        .max(4_000_000)
        .describe('The whole file, header first.'),
})
    .describe('POST /curriculum/import/preview and POST /curriculum/import');
export const columnMappingSchema = z.strictObject({
    header: z.string().describe('The header, as the file wrote it.'),
    field: plannedFieldSchema
        .nullable()
        .describe('The named column it fills, or null when it is kept as extra.'),
});
export const importIssueSchema = z.strictObject({
    line: z
        .number()
        .int()
        .min(1)
        .nullable()
        .describe('1-based, as a spreadsheet counts; null for the header.'),
    message: z
        .string()
        .describe('One problem, in words the file’s author can act on.'),
});
export const proposedModuleSchema = z.strictObject({
    name: z.string(),
    lessons: z.number().int().min(0).describe('Rows in the file under it.'),
    isNew: z.boolean().describe('Not in the plan yet; applying creates it.'),
});
export const proposedCourseSchema = z.strictObject({
    name: z.string(),
    isNew: z.boolean().describe('Not in the plan yet; applying creates it.'),
    modules: z.array(proposedModuleSchema),
});
export const importChangeSchema = z.strictObject({
    lessonId: z.string(),
    field: plannedFieldSchema
        .nullable()
        .describe('The named column, or null for an extra one.'),
    column: z.string().describe('The header the change is under.'),
    before: z.string(),
    after: z.string(),
    kept: z
        .boolean()
        .describe('An admin edited this field, so applying leaves it as it is.'),
});
export const importPreviewSchema = z
    .strictObject({
    rowCount: z.number().int().min(0),
    columns: z.array(columnMappingSchema),
    courses: z.array(proposedCourseSchema),
    issues: z
        .array(importIssueSchema)
        .describe('A header problem stops the import; a line with a problem is skipped and the rest applied.'),
    additions: z
        .array(z.string())
        .describe('Lesson IDs the plan does not have yet, skipped lines aside.'),
    changes: z.array(importChangeSchema),
    missing: z
        .array(z.string())
        .describe('Lesson IDs in the plan but not in this file. Import never deletes them.'),
})
    .describe('POST /curriculum/import/preview');
export const importResultSchema = z
    .strictObject({
    added: z.number().int().min(0).describe('Planned lessons created.'),
    changed: z
        .number()
        .int()
        .min(0)
        .describe('Planned lessons with at least one field updated.'),
    kept: z
        .number()
        .int()
        .min(0)
        .describe('Changes skipped because an admin edited the field.'),
    coursesAdded: z.number().int().min(0),
    modulesAdded: z.number().int().min(0),
    total: z
        .number()
        .int()
        .min(0)
        .describe('Planned lessons after the import.'),
    skipped: z
        .array(importIssueSchema)
        .describe('Why each line left out was left out.'),
})
    .describe('POST /curriculum/import');
export const plannedLessonSortSchema = z
    .enum([
    'position',
    'lessonId',
    'course',
    'module',
    'name',
    'status',
    'published',
    'publishedAt',
    'updatedAt',
])
    .describe('`position` is curriculum order; sorting never changes it.');
const yesNoSchema = z.enum(['yes', 'no']);
export const MAX_LESSON_PAGE = 100;
export const plannedLessonQuerySchema = z
    .strictObject({
    q: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .optional()
        .describe('Matched case-insensitively against the Lesson ID, course, module and lesson names, description and writer brief.'),
    courseId: z.string().min(1).optional().describe('A planned course id.'),
    moduleId: z.string().min(1).optional().describe('A planned module id.'),
    status: plannedLessonStatusSchema.optional(),
    published: yesNoSchema.optional(),
    publishedFrom: z.iso
        .date()
        .optional()
        .describe('Published on or after this day, YYYY-MM-DD.'),
    publishedTo: z.iso
        .date()
        .optional()
        .describe('Published on or before this day, YYYY-MM-DD.'),
    sort: plannedLessonSortSchema.default('position'),
    order: z.enum(['asc', 'desc']).default('asc'),
    limit: z.coerce.number().int().min(1).max(MAX_LESSON_PAGE).default(25),
    offset: z.coerce.number().int().min(0).default(0),
})
    .describe('GET /curriculum/lessons');
/** What narrows the list, without paging or sorting — the list's filter and the chat's. */
export const plannedLessonFilterSchema = plannedLessonQuerySchema.pick({
    q: true,
    courseId: true,
    moduleId: true,
    status: true,
    published: true,
    publishedFrom: true,
    publishedTo: true,
});
export const exportModeSchema = z
    .enum(['page', 'filtered', 'selected', 'all'])
    .describe('`page` is limit and offset applied; `filtered` every match; `selected` the `ids`, or every match when none are given; `all` the whole plan.');
export const plannedLessonExportQuerySchema = plannedLessonQuerySchema
    .extend({
    mode: exportModeSchema,
    ids: z
        .string()
        .min(1)
        .optional()
        .describe('Comma-separated Lesson IDs, for `selected`.'),
    exclude: z
        .string()
        .min(1)
        .optional()
        .describe('Comma-separated Lesson IDs left out of `selected` when it is every match.'),
})
    .describe('GET /curriculum/lessons/export');
/** One module the built lesson sits in, in the app — CURR-20. */
export const appPlacementSchema = z.strictObject({
    moduleCollectionId: z.string(),
    module: z.string(),
    courseCollectionId: z.string().nullable(),
    course: z.string().nullable(),
    plannedModuleId: z
        .string()
        .nullable()
        .describe('The planned module linked to this app module, if the plan has one.'),
});
export const plannedLessonSchema = z.strictObject({
    id: z.string(),
    lessonId: z.string(),
    position: z.number().int().min(1).describe('Curriculum order.'),
    courseId: z.string(),
    course: z.string(),
    courseCollectionId: z
        .string()
        .nullable()
        .describe('The course created in the app, once there is one.'),
    moduleId: z.string(),
    module: z.string(),
    moduleCollectionId: z
        .string()
        .nullable()
        .describe('The module created in the app, once there is one.'),
    name: z.string(),
    description: z.string(),
    writerBrief: z.string(),
    storyBeat: z.string().describe('Where this lesson sits in its module story.'),
    moduleStoryRevision: z
        .number()
        .int()
        .min(0)
        .describe('Its module story’s revision, which a beat save sends back.'),
    builtStoryRevision: z
        .number()
        .int()
        .min(0)
        .nullable()
        .describe('The module story revision its built lesson was written from or last reviewed against; null when built without one — CURR-40.'),
    storyChanged: z
        .boolean()
        .describe('Its module story was saved again since its built lesson was written or reviewed. Cleared by a rebuild or POST …/story-review.'),
    primarySourceName: z.string(),
    primarySourceUrl: z.string(),
    additionalSourceName: z.string(),
    additionalSourceUrl: z.string(),
    extra: z.array(extraColumnSchema),
    status: plannedLessonStatusSchema,
    builtLessonId: z
        .string()
        .nullable()
        .describe('The lesson built from this row, once there is one.'),
    buildJobId: z
        .string()
        .nullable()
        .describe('The generation job of its latest build, for the job page.'),
    buildStep: buildStepSchema.nullable(),
    rebuilding: z
        .boolean()
        .describe('Its newest job rebuilds the lesson it already has and has not settled, review included — CURR-35.'),
    buildError: z
        .string()
        .nullable()
        .describe('Why its build failed, while its status is Build failed.'),
    published: z.boolean().describe('A version is live now.'),
    publishedAt: z.iso.datetime().nullable(),
    appPlacements: z
        .array(appPlacementSchema)
        .describe('Where the built lesson sits in the app now. Empty until it is built, and while it is archived.'),
    placementMismatch: z
        .boolean()
        .describe('The built lesson sits somewhere other than its planned module. Resolved by either placement sync.'),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** A planned lesson's writer brief and sources. */
export const plannedBriefSchema = plannedLessonSchema.pick({
    writerBrief: true,
    primarySourceName: true,
    primarySourceUrl: true,
    additionalSourceName: true,
    additionalSourceUrl: true,
});
export const plannedLessonListSchema = z
    .strictObject({
    lessons: z.array(plannedLessonSchema),
    total: z.number().int().min(0).describe('Every match, across pages.'),
})
    .describe('GET /curriculum/lessons');
/** The most lessons one build takes — the milestone's answer: refused, never truncated. */
export const MAX_BUILD = 50;
const lessonIds = z.array(curriculumLessonIdSchema).min(1);
export const buildPreflightRequestSchema = z
    .strictObject({
    lessonIds: lessonIds
        .max(5000)
        .describe('Lesson IDs. More than the build limit is reported, not refused.'),
})
    .describe('POST /curriculum/builds/preflight');
/** Whether a job researches each module before its outline — GEN-218, spec 018 §8. */
const researchField = z
    .boolean()
    .default(false)
    .describe('Research each module before its outline. A module with an evidence pack keeps it.');
export const buildRequestSchema = z
    .strictObject({
    lessonIds: lessonIds
        .max(MAX_BUILD, `A build takes at most ${String(MAX_BUILD)} lessons. Choose up to ${String(MAX_BUILD)} and build the rest as another batch.`)
        .describe('Lesson IDs to build.'),
    verify: z
        .boolean()
        .default(false)
        .describe('Verify each lesson against its sources once the build has written it — CURR-31.'),
    research: researchField,
})
    .describe('POST /curriculum/builds');
// A rebuild took no body before GEN-218; an absent one still means research off.
export const rebuildRequestSchema = z
    .strictObject({ research: researchField })
    .prefault({})
    .describe('POST /curriculum/lessons/{lessonId}/rebuild');
const plannedRef = z.strictObject({ id: z.string(), name: z.string() });
const plannedModuleRef = plannedRef.extend({ course: z.string() });
export const buildPreflightSchema = z
    .strictObject({
    count: z.number().int().min(0).describe('Distinct lessons selected.'),
    limit: z.literal(MAX_BUILD),
    coursesToCreate: z.array(plannedRef),
    modulesToCreate: z.array(plannedModuleRef),
    modulesInApp: z
        .array(plannedModuleRef)
        .describe('Modules already created in the app, which the build reuses.'),
    unavailable: z
        .array(z.strictObject({
        lessonId: z.string(),
        status: plannedLessonStatusSchema,
    }))
        .describe('Already queued, building, built or published.'),
    blocked: z
        .array(z.strictObject({ lessonId: z.string(), issues: z.array(z.string()) }))
        .describe('What stops each of these from building.'),
    warnings: z.array(z.strictObject({ lessonId: z.string(), message: z.string() })),
    unknown: z.array(z.string()).describe('IDs that are not in the plan.'),
    storyMissing: z
        .array(plannedModuleRef)
        .describe('Modules of the selection with no saved story. The build drafts one, with figures and a beat for each lesson, before their lessons are written — CURR-58.'),
    castMissing: z
        .array(plannedModuleRef)
        .describe('Modules of the selection with no cast. The build drafts one with a lead before their lessons are written — CURR-58.'),
    estimate: z.strictObject({
        minutes: z.number().int().min(0),
        label: z.string(),
    }),
    ready: z
        .boolean()
        .describe('Within the limit, and nothing above stops the build.'),
})
    .describe('What a build would do, without doing it.');
export const buildSchema = z
    .strictObject({
    jobId: z.string(),
    created: z
        .boolean()
        .describe('False when this exact selection was already submitted: the earlier job is returned.'),
    lessons: z.number().int().min(1),
})
    .describe('The build job.');
export const curriculumVerifyRequestSchema = z
    .strictObject({
    lessonIds: lessonIds
        .max(MAX_BUILD)
        .describe('Lesson IDs; the built ones are verified.'),
})
    .describe('POST /curriculum/verifications');
export const curriculumVerifySchema = z
    .strictObject({
    jobId: z.string(),
    lessons: z.number().int().min(1).describe('Lessons the job verifies.'),
    notBuilt: z
        .array(z.string())
        .describe('Lesson IDs left out because nothing has been built from them.'),
})
    .describe('The verify job — CURR-31.');
export const createInAppRequestSchema = z
    .strictObject({
    courseIds: z.array(z.string().min(1)).max(500).default([]),
    moduleIds: z.array(z.string().min(1)).max(500).default([]),
})
    .describe('POST /curriculum/create-in-app');
const linked = z.strictObject({
    id: z.string().describe('The planned course or module.'),
    collectionId: z.string(),
    created: z.boolean().describe('False when it was already linked.'),
});
export const createdInAppSchema = z
    .strictObject({ courses: z.array(linked), modules: z.array(linked) })
    .describe('The collections each planned course and module is linked to.');
export const nextLessonIdSchema = z
    .strictObject({ lessonId: z.string() })
    .describe('GET /curriculum/modules/:id/next-lesson-id — what adding a lesson there would be given.');
export const lessonBuildStateSchema = z
    .strictObject({ lessonId: z.string(), status: plannedLessonStatusSchema })
    .describe('A planned lesson after a retry or a cancel.');
export const lessonVersionSchema = z
    .strictObject({
    id: z.string(),
    reason: lessonVersionReasonSchema,
    jobId: z
        .string()
        .optional()
        .describe('The rebuild job that replaced it. Absent for a restore.'),
    title: z.string().describe('The lesson title this version had.'),
    scenes: z.number().int().min(0),
    createdAt: z.iso.datetime(),
})
    .describe('The content a lesson had before a rebuild or a restore replaced it.');
export const lessonVersionsSchema = z
    .strictObject({
    lessonId: z.string().describe('The planned Lesson ID.'),
    builtLessonId: z.string(),
    versions: z.array(lessonVersionSchema).describe('Newest first.'),
})
    .describe('GET /curriculum/lessons/:lessonId/versions — CURR-34. Each one can be restored.');
/* Planning edits — CURR-17. Spec §5, §6 and §9. */
const planningName = z.string().trim().min(1).max(200);
/** An http or https address; what the import and the edit forms both accept. */
export const isWebAddress = (value) => URL.canParse(value) && /^https?:$/.test(new URL(value).protocol);
const webAddress = z
    .string()
    .trim()
    .max(2_000)
    .refine((value) => value === '' || isWebAddress(value), 'Use a full web address starting with http:// or https://, or leave it empty.');
/** The card's five headings a module story is written under — CURR-39. */
export const STORY_HEADINGS = [
    'Character and role:',
    'Business and setting:',
    'Starting goal and challenge:',
    'Important facts and constraints:',
    'Story development by lesson:',
];
// Not trimmed: a story reads back exactly as the author wrote it.
export const storySchema = z
    .string()
    .max(50_000)
    .refine((story) => story.trim() !== '', 'Write the module story.');
export const storyBeatSchema = z.string().max(10_000);
export const storyRevisionSchema = z
    .number()
    .int()
    .min(0)
    .describe('The module story revision the edit was made from. A stale one is refused with a 409.');
const position = z
    .number()
    .int()
    .min(1)
    .describe('1-based place among its siblings; past the end means last. Others shift to make room.');
export const plannedLessonIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .describe('The curriculum Lesson ID. Never changes, and never goes to a different lesson.');
/* A module's cast — CAST-06. Saved through the story revision, like the story itself. */
export const moduleCastSchema = z
    .array(castAssignmentSchema)
    .max(3)
    .superRefine((cast, ctx) => {
    const fail = (message) => {
        ctx.addIssue({ code: 'custom', message });
    };
    if (new Set(cast.map((one) => one.id)).size !== cast.length)
        fail('Each assignment needs its own id.');
    const characters = cast.flatMap((one) => one.character ?? []);
    if (new Set(characters).size !== characters.length)
        fail('A character plays one role in a module.');
    if (cast.length === 0)
        return;
    const leads = cast.filter((one) => one.part === 'lead');
    if (leads.length !== 1)
        fail('A module has one lead. To remove the lead, name a new one or make the lead a one-off.');
    const [lead] = leads;
    if (lead?.character !== undefined && isAnimal(lead.character))
        fail('The lead is a person, not an animal.');
})
    .describe('Up to three, one of them the lead. Empty means the module casts nobody.');
/** The column is Json, so the schema says what it holds; a row that no longer parses casts nobody. */
export const savedCast = (json) => moduleCastSchema.safeParse(json).data ?? [];
/** Why a saved cast does not parse, as a sentence, so it is not read as casting nobody — CURR-59. */
export function castProblem(json) {
    const [issue] = moduleCastSchema.safeParse(json).error?.issues ?? [];
    if (issue === undefined)
        return null;
    const at = issue.path.join('.');
    const said = at === '' ? issue.message : `${at}: ${issue.message}`;
    return /[.!?]$/.test(said) ? said : `${said}.`;
}
/** The assignments a lesson uses: those its subset names, or every one when it names none. */
export const lessonAssignments = (cast, subset) => subset.length === 0 ? cast : cast.filter((one) => subset.includes(one.id));
/**
 * The cast a lesson built in this module is checked against at publish: the assignments
 * its subset names, or every one when it names none. Undefined when the module casts
 * nobody, which leaves the lesson's own cast in charge. One-offs have no figure to draw.
 */
export function moduleLessonCast(cast, subset) {
    if (cast.length === 0)
        return undefined;
    return lessonAssignments(cast, subset).flatMap(({ character, role }) => character === undefined ? [] : [{ character, role }]);
}
export const lessonCastSubsetSchema = z.strictObject({
    lessonId: plannedLessonIdSchema,
    cast: lessonCastIdsSchema,
});
const lessonContent = {
    description: z.string().max(20_000),
    writerBrief: z.string().max(50_000),
    primarySourceName: z.string().trim().max(500),
    primarySourceUrl: webAddress,
    additionalSourceName: z.string().trim().max(500),
    additionalSourceUrl: webAddress,
};
export const createPlannedCourseSchema = z
    .strictObject({ name: planningName })
    .describe('POST /curriculum/courses. Lands last; a planning row only.');
export const updatePlannedCourseSchema = z
    .strictObject({
    name: planningName.optional(),
    position: position.optional(),
})
    .describe('PATCH /curriculum/courses/:id');
export const createPlannedModuleSchema = z
    .strictObject({
    courseId: z.string().min(1),
    name: planningName,
    story: storySchema.describe('Required: every new module has a story.'),
})
    .describe('POST /curriculum/modules. Lands last in its course.');
export const updatePlannedModuleSchema = z
    .strictObject({
    name: planningName.optional(),
    courseId: z
        .string()
        .min(1)
        .optional()
        .describe('Moves the module, last in that course unless `position` says.'),
    position: position.optional(),
    story: storySchema.optional(),
    figures: storyFiguresSchema
        .optional()
        .describe('Every figure, replacing what is there. Empty clears them.'),
    writerNotes: writerNotesSchema.optional(),
    cast: moduleCastSchema
        .optional()
        .describe('Every assignment, replacing what is there. Removing one a lesson still uses is refused.'),
    lessonCasts: z
        .array(lessonCastSubsetSchema)
        .max(500)
        .refine((all) => new Set(all.map((one) => one.lessonId)).size === all.length, 'Name each lesson once.')
        .optional()
        .describe('The subsets of the lessons named, each replacing what is there. Others keep theirs.'),
    storyRevision: storyRevisionSchema
        .optional()
        .describe('Required with `story`, `figures`, `writerNotes`, `cast` or `lessonCasts`.'),
})
    .describe('PATCH /curriculum/modules/:id');
export const createPlannedLessonSchema = z
    .strictObject({
    moduleId: z.string().min(1),
    lessonId: plannedLessonIdSchema
        .optional()
        .describe('Omit to have one generated in the CSV’s format.'),
    name: planningName,
    description: lessonContent.description.default(''),
    writerBrief: lessonContent.writerBrief.default(''),
    primarySourceName: lessonContent.primarySourceName.default(''),
    primarySourceUrl: lessonContent.primarySourceUrl.default(''),
    additionalSourceName: lessonContent.additionalSourceName.default(''),
    additionalSourceUrl: lessonContent.additionalSourceUrl.default(''),
})
    .describe('POST /curriculum/lessons. Lands last in its module.');
export const updatePlannedLessonSchema = z
    .strictObject({
    name: planningName.optional(),
    description: lessonContent.description.optional(),
    writerBrief: lessonContent.writerBrief.optional(),
    primarySourceName: lessonContent.primarySourceName.optional(),
    primarySourceUrl: lessonContent.primarySourceUrl.optional(),
    additionalSourceName: lessonContent.additionalSourceName.optional(),
    additionalSourceUrl: lessonContent.additionalSourceUrl.optional(),
    storyBeat: storyBeatSchema
        .optional()
        .describe('Saving a changed beat bumps its module’s story revision.'),
    storyRevision: storyRevisionSchema
        .optional()
        .describe('Required with `storyBeat`.'),
    extra: z
        .array(extraColumnSchema)
        .optional()
        .describe('Every extra column, replacing what is there.'),
    moduleId: z
        .string()
        .min(1)
        .optional()
        .describe('Moves the lesson, last in that module unless `position` says.'),
    position: position.optional(),
})
    .describe('PATCH /curriculum/lessons/:lessonId. The Lesson ID cannot change.');
export const storyReviewSchema = z
    .strictObject({
    storyRevision: storyRevisionSchema.describe('The module story revision the author reviewed the lesson against. A stale one is refused with a 409.'),
})
    .describe('POST /curriculum/lessons/:lessonId/story-review');
export const appDifferenceSchema = z
    .enum(['name', 'course'])
    .describe('How a linked planned row differs from its app counterpart. Nothing syncs on its own.');
export const plannedModuleSchema = z.strictObject({
    id: z.string(),
    courseId: z.string(),
    name: z.string(),
    position: z.number().int().min(1).describe('Its place in its course.'),
    lessons: z.number().int().min(0),
    collectionId: z
        .string()
        .nullable()
        .describe('The module in the app, once created.'),
    differsFromApp: z.array(appDifferenceSchema),
});
export const plannedModuleStorySchema = z
    .strictObject({
    id: z.string(),
    name: z.string(),
    story: z.string(),
    figures: storyFiguresSchema,
    writerNotes: writerNotesSchema,
    storyRevision: storyRevisionSchema.describe('0 until a story is saved.'),
    cast: z.array(castAssignmentSchema),
    castProblem: z
        .string()
        .nullable()
        .describe('Why the saved cast does not parse, when it does not: `cast` is then empty.'),
    lessons: z
        .array(lessonCastSubsetSchema.extend({
        built: lessonCastSchema.describe('The cast its built lesson names, empty until it names one — what adopting a cast starts from.'),
    }))
        .describe('Every lesson in the module, in order, with its subset.'),
})
    .describe('GET /curriculum/modules/:id/story');
export const plannedCourseSchema = z.strictObject({
    id: z.string(),
    name: z.string(),
    position: z.number().int().min(1),
    collectionId: z
        .string()
        .nullable()
        .describe('The course in the app, once created.'),
    differsFromApp: z.array(appDifferenceSchema),
    modules: z.array(plannedModuleSchema),
});
export const plannedCourseListSchema = z
    .strictObject({ courses: z.array(plannedCourseSchema) })
    .describe('GET /curriculum/courses — the plan’s hierarchy, in curriculum order.');
export const planningEntitySchema = z.enum(['COURSE', 'MODULE', 'LESSON']);
export const planningActionSchema = z
    .enum(['CREATE', 'UPDATE', 'DELETE', 'REVERT'])
    .describe('CREATE and DELETE are history only; UPDATE and REVERT can be reverted.');
/** One shape for all three kinds; a field a kind does not have is null. */
export const planningSnapshotSchema = z.strictObject({
    name: z.string(),
    parentId: z
        .string()
        .nullable()
        .describe('A module’s course, a lesson’s module; null for a course.'),
    position: z.number().int().min(1).describe('Its place among its siblings.'),
    lessonId: z.string().nullable(),
    description: z.string().nullable(),
    writerBrief: z.string().nullable(),
    primarySourceName: z.string().nullable(),
    primarySourceUrl: z.string().nullable(),
    additionalSourceName: z.string().nullable(),
    additionalSourceUrl: z.string().nullable(),
    extra: z.array(extraColumnSchema).nullable(),
    editedFields: z.array(z.string()).nullable(),
});
export const planningRevisionSchema = z.strictObject({
    id: z.string(),
    entity: planningEntitySchema,
    entityId: z
        .string()
        .describe('The planned row’s id, kept after it is removed.'),
    lessonId: z.string().nullable(),
    action: planningActionSchema,
    before: planningSnapshotSchema.nullable(),
    after: planningSnapshotSchema.nullable(),
    batchId: z
        .string()
        .nullable()
        .describe('Shared by the revisions one action made; they revert together.'),
    revertOfId: z.string().nullable(),
    actorId: z.string(),
    createdAt: z.iso.datetime(),
});
export const planningRevisionQuerySchema = z
    .strictObject({
    entity: planningEntitySchema.optional(),
    entityId: z.string().min(1).optional(),
    lessonId: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
})
    .describe('GET /curriculum/revisions — newest first.');
export const planningRevisionListSchema = z
    .strictObject({
    revisions: z.array(planningRevisionSchema),
    total: z.number().int().min(0),
})
    .describe('GET /curriculum/revisions');
export const revertResultSchema = z
    .strictObject({
    revisions: z
        .array(planningRevisionSchema)
        .describe('The REVERT revisions written, one per row restored.'),
    notes: z
        .array(z.string())
        .describe('What could not be restored, in words an admin can act on.'),
})
    .describe('POST /curriculum/revisions/:id/revert');
export const linkedContentSchema = z.strictObject({
    id: z.string(),
    title: z.string(),
    status: z
        .string()
        .describe('DRAFT, PUBLISHED or ARCHIVED; APPROVED for an approved draft lesson.'),
});
export const deletionPreviewSchema = z
    .strictObject({
    courses: z.number().int().min(0),
    modules: z.number().int().min(0),
    lessons: z.number().int().min(0),
    lessonIds: z.array(z.string()).describe('Every planned lesson removed.'),
    linkedCollections: z
        .array(linkedContentSchema)
        .describe('App courses and modules linked to what is removed. They stay.'),
    linkedLessons: z
        .array(linkedContentSchema)
        .describe('App lessons built from what is removed. They stay.'),
    cancelledBuilds: z
        .array(z.string())
        .describe('Lesson IDs whose queued build is cancelled before the delete.'),
    leftEmpty: z
        .array(z.string())
        .describe('App modules left with no lesson but archived ones if the draft lessons are archived too.'),
    affects: z
        .literal('planning')
        .describe('App content stays, unless a planned lesson is removed with `archiveDraft`.'),
    blocked: z.boolean(),
    message: z
        .string()
        .nullable()
        .describe('Why it is blocked, or null when it can go ahead.'),
})
    .describe('What a planning deletion removes, and whether it may.');
export const plannedLessonDeletionQuerySchema = z
    .strictObject({
    archiveDraft: yesNoSchema
        .default('no')
        .describe('Also archive its built lesson when that is an unapproved draft. Archived lessons can be restored in Lessons.'),
})
    .describe('DELETE /curriculum/lessons/:lessonId');
const linkedRecordSchema = z
    .strictObject({
    id: z.string(),
    title: z.string(),
    status: contentStatusSchema,
})
    .describe('The app course module or lesson this planned row is linked to.');
const renameReviewFields = {
    id: z.string(),
    proposedName: z.string(),
    reason: z.string().describe('Why this name, from reading the row’s content.'),
    linked: linkedRecordSchema.nullable(),
    renameLinked: z
        .boolean()
        .describe('Approving also renames the linked app record. Off by default.'),
    touches: z
        .enum(['planning', 'both'])
        .describe('What approving changes: the planning record only, or the linked app record too.'),
    skipped: z.boolean(),
    applied: z
        .boolean()
        .describe('The planned record already has the proposed name.'),
};
export const moduleRenameSchema = z.strictObject({
    ...renameReviewFields,
    course: z.string().describe('The course, as the CSV named it.'),
    module: z.string().describe('The module, as the CSV named it.'),
    moduleId: z
        .string()
        .nullable()
        .describe('The planned module it renames; null when the plan has none.'),
    currentName: z.string().nullable(),
    lessons: z.array(z.string()).describe('The module’s lesson names, in order.'),
    matches: z
        .array(z.strictObject({
        moduleId: z.string().nullable(),
        course: z.string(),
        currentName: z.string().nullable(),
    }))
        .describe('The other modules the CSV gave the same name.'),
});
export const moduleRenameListSchema = z
    .strictObject({
    renames: z.array(moduleRenameSchema),
    pending: z
        .number()
        .int()
        .min(0)
        .describe('How many approving all would rename.'),
})
    .describe('GET /curriculum/module-renames');
export const lessonRenameSchema = z.strictObject({
    ...renameReviewFields,
    lessonId: curriculumLessonIdSchema,
    name: z.string().describe('The title, as the CSV named it.'),
    course: z.string().nullable(),
    module: z.string().nullable(),
    currentName: z
        .string()
        .nullable()
        .describe('The planned lesson’s title; null when the plan has none.'),
    description: z.string(),
    matches: z
        .array(z.strictObject({
        lessonId: curriculumLessonIdSchema,
        course: z.string().nullable(),
        module: z.string().nullable(),
        currentName: z.string().nullable(),
    }))
        .describe('The other lessons the CSV gave the same title.'),
});
export const lessonRenameListSchema = z
    .strictObject({
    renames: z.array(lessonRenameSchema),
    pending: z
        .number()
        .int()
        .min(0)
        .describe('How many approving all would rename.'),
})
    .describe('GET /curriculum/lesson-renames');
export const updateRenameSchema = z
    .strictObject({
    proposedName: planningName.optional(),
    renameLinked: z.boolean().optional(),
    skipped: z.boolean().optional(),
})
    .describe('PATCH /curriculum/module-renames/:id and lesson-renames/:id');
export const applyRenamesResultSchema = z
    .strictObject({
    renamed: z.number().int().min(0),
    batchId: z
        .string()
        .nullable()
        .describe('Revert any one revision of this batch to undo all of them.'),
})
    .describe('POST …/apply and …/:id/apply. Reverting restores the planned names only.');
const syncOptionSchema = z.strictObject({
    available: z.boolean(),
    from: z.string().nullable().describe('Course › module it moves from.'),
    to: z.string().nullable().describe('Course › module it moves to.'),
    message: z
        .string()
        .describe('What applying it does, or why it cannot be applied.'),
});
export const placementSchema = z
    .strictObject({
    lessonId: z.string(),
    builtLessonId: z.string().nullable(),
    planned: z.strictObject({
        moduleId: z.string(),
        module: z.string(),
        course: z.string(),
        moduleCollectionId: z.string().nullable(),
    }),
    app: z.array(appPlacementSchema),
    mismatch: z.boolean(),
    live: z.boolean().describe('The built lesson is published now.'),
    updatePlan: syncOptionSchema.describe('Move the planned lesson to the module the app has it in.'),
    moveAppLesson: syncOptionSchema.describe('Move the app lesson to the module the plan has it in.'),
})
    .describe('GET /curriculum/lessons/:lessonId/placement — both syncs, previewed.');
/** How many history entries the details carry — the open question's proposal. */
export const DETAILS_HISTORY = 10;
export const learnerPeriodSchema = z
    .enum(['all', '7d', '30d'])
    .describe('All time, or the last 7 or 30 days up to now.');
export const plannedLessonDetailsQuerySchema = z
    .strictObject({ period: learnerPeriodSchema.default('all') })
    .describe('GET /curriculum/lessons/:lessonId');
export const lessonHistoryKindSchema = z.enum([
    'CREATED',
    'EDITED',
    'REVERTED',
    'BUILD_QUEUED',
    'BUILT',
    'BUILD_FAILED',
    'PUBLISHED',
    'UNPUBLISHED',
    'SYNCED',
]);
export const lessonHistoryEntrySchema = z.strictObject({
    at: z.iso.datetime(),
    kind: lessonHistoryKindSchema,
    what: z.string().describe('What changed, in words.'),
    who: z
        .string()
        .nullable()
        .describe('The admin’s handle, or "AI build" for a build’s outcome; null where nobody was recorded.'),
    revisionId: z
        .string()
        .nullable()
        .describe('The planning revision an edit or revert wrote, for POST /curriculum/revisions/:id/revert.'),
});
export const plannedLessonDetailsSchema = z
    .strictObject({
    lesson: plannedLessonSchema.describe('Planning data, course and module links, and the computed status.'),
    build: z.strictObject({
        builtAt: z.iso
            .datetime()
            .nullable()
            .describe('When the build that wrote the lesson finished.'),
        planningRevisionId: z
            .string()
            .nullable()
            .describe('The newest planning revision when that build was queued; null if the row had none, as imported.'),
        planningChangedSince: z
            .boolean()
            .nullable()
            .describe('A field the build reads — name, description, writer brief, sources — differs from what it read. Null until built.'),
        latestFailure: z
            .strictObject({
            at: z.iso.datetime(),
            message: z.string(),
            jobId: z.string(),
        })
            .nullable()
            .describe('The newest failed build, whatever came after it.'),
    }),
    approval: z
        .strictObject({
        status: reviewStatusSchema.nullable(),
        reason: z.string().nullable(),
        by: z.string().nullable(),
        at: z.iso.datetime().nullable(),
    })
        .nullable()
        .describe('The built lesson’s review; null until built.'),
    publications: z
        .array(z.strictObject({
        kind: publishEventKindSchema,
        version: z.number().int().min(1),
        at: z.iso.datetime(),
    }))
        .describe('Every publish and unpublish, newest first.'),
    links: z
        .strictObject({
        builtLessonId: z.string(),
        version: z.number().int().min(1),
        editorPath: z
            .string()
            .describe('The admin’s lesson editor, whose preview pane is the viewer: the viewer has no lesson URL of its own.'),
    })
        .nullable()
        .describe('Null until built.'),
    history: z
        .array(lessonHistoryEntrySchema)
        .max(DETAILS_HISTORY)
        .describe('The most recent major changes, newest first: edits, builds, publications, syncs.'),
    learners: z
        .strictObject({
        period: learnerPeriodSchema,
        from: z.iso.datetime().nullable().describe('Null for all time.'),
        to: z.iso.datetime(),
        started: z
            .number()
            .int()
            .min(0)
            .describe('Unique learners who started it in the period.'),
        completed: z
            .number()
            .int()
            .min(0)
            .describe('Of those, unique learners who reached the end, any score.'),
    })
        .nullable()
        .describe('Across every version of the built lesson. Null means not available: the read failed.'),
})
    .describe('GET /curriculum/lessons/:lessonId');
/**
 * Whether a module has a story to build or fix with — GEN-129. A beat saved before any
 * story bumps the revision too, so the text is checked as well.
 */
export const hasStory = ({ story, storyRevision, }) => storyRevision > 0 && story.trim() !== '';
/**
 * A module's story as a build or a fix captures it — GEN-129, GEN-151. Writer notes
 * alone still count, so a module with notes and no story reaches the agents — GEN-193.
 */
export const capturedStory = (module) => {
    const story = hasStory(module);
    const notes = module.writerNotes.trim();
    if (!story && (module.storyRevision === 0 || notes === ''))
        return undefined;
    return {
        ...(story && { text: module.story }),
        revision: module.storyRevision,
        ...(story && module.figures.length > 0 && { figures: module.figures }),
        ...(notes !== '' && { notes }),
    };
};
/** A planned module's story and cast as a build captures them. Revision 0 is no story. */
export const capturedModule = (module) => {
    const cast = savedCast(module.cast);
    return {
        story: capturedStory(module),
        cast: cast.length === 0 ? undefined : cast,
    };
};
/** A planned lesson's beat and cast subset as a build captures them — CAST-06. */
export const capturedBeat = (lesson) => ({
    storyBeat: lesson.storyBeat.trim() || undefined,
    cast: lesson.cast.length === 0 ? undefined : [...lesson.cast],
});
//# sourceMappingURL=curriculum.js.map